import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { Trash2, Plus } from "react-feather";
import { fetchSuppliers } from "../../redux/slices/supplierSlice";
import { fetchProducts } from "../../redux/slices/productSlice";
import { createPurchaseReturnThunk } from "../../redux/slices/purchaseSlice";

const today = () => new Date().toISOString().split("T")[0];
const EMPTY_ITEM = { productID: "", quantity: "", purchaseRate: "" };
const EMPTY_FORM = { supplierID: "", returnDate: today(), invoiceReference: "", remarks: "" };

const fmt = (v) => Number(v || 0).toFixed(2);
const rowTotal = (r) => (Number(r.quantity) || 0) * (Number(r.purchaseRate) || 0);

const PurchaseReturnModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);
  const products = useSelector((s) => s.products.list);
  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);

  const supplierOptions = suppliers
    .filter((s) => s.status === "Active")
    .map((s) => ({ value: s.supplierID || s.id, label: s.supplierName }));

  const productOptions = products
    .filter((p) => p.status === "Active")
    .map((p) => ({ value: p.productID || p.id, label: p.itemName, rate: p.purchaseRate }));

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (show) { setForm({ ...EMPTY_FORM, returnDate: today() }); setItems([{ ...EMPTY_ITEM }]); }
  }, [show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setItem = (idx, field) => (e) =>
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: e.target.value } : r)));
  const setItemProduct = (idx) => (opt) =>
    setItems((rows) =>
      rows.map((r, i) =>
        i === idx ? { ...r, productID: opt?.value || "", purchaseRate: opt?.rate || "" } : r
      )
    );

  const addRow = () => setItems((rows) => [...rows, { ...EMPTY_ITEM }]);
  const removeRow = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));
  const grandTotal = items.reduce((sum, r) => sum + rowTotal(r), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplierID) { Swal.fire("Required", "Please select a supplier.", "warning"); return; }
    const validItems = items.filter((r) => r.productID && r.quantity && r.purchaseRate);
    if (!validItems.length) { Swal.fire("Required", "Add at least one product line.", "warning"); return; }
    setSaving(true);
    try {
      await dispatch(createPurchaseReturnThunk({ ...form, items: validItems, totalAmount: grandTotal })).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Purchase return recorded", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch {
      Swal.fire("Error", "Failed to save purchase return.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title"><h4>New Purchase Return</h4></div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-lg-5 mb-3">
                    <label className="form-label">Supplier <span className="text-danger">*</span></label>
                    <Select classNamePrefix="react-select" options={supplierOptions}
                      value={supplierOptions.find((o) => o.value === form.supplierID) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, supplierID: opt?.value || "" }))}
                      placeholder="Select supplier" />
                  </div>
                  <div className="col-lg-3 mb-3">
                    <label className="form-label">Return Date</label>
                    <input type="date" className="form-control" value={form.returnDate} onChange={set("returnDate")} />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Original Invoice / Reference</label>
                    <input className="form-control" value={form.invoiceReference} onChange={set("invoiceReference")} placeholder="Original bill / invoice no." />
                  </div>
                </div>

                <div className="table-responsive mb-3">
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 200 }}>Product <span className="text-danger">*</span></th>
                        <th style={{ width: 90 }}>Qty <span className="text-danger">*</span></th>
                        <th style={{ width: 110 }}>Rate (₹) <span className="text-danger">*</span></th>
                        <th style={{ width: 110 }} className="text-end">Total (₹)</th>
                        <th style={{ width: 48 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <Select classNamePrefix="react-select" options={productOptions}
                              value={productOptions.find((o) => o.value === row.productID) || null}
                              onChange={setItemProduct(idx)}
                              placeholder="Select product" menuPosition="fixed"
                              styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                          </td>
                          <td><input type="number" className="form-control form-control-sm" min={1}
                            value={row.quantity} onChange={setItem(idx, "quantity")} placeholder="0" /></td>
                          <td><input type="number" className="form-control form-control-sm" min={0} step="0.01"
                            value={row.purchaseRate} onChange={setItem(idx, "purchaseRate")} placeholder="0.00" /></td>
                          <td className="text-end align-middle fw-semibold">₹{fmt(rowTotal(row))}</td>
                          <td className="text-center align-middle">
                            {items.length > 1 && (
                              <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeRow(idx)}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="text-end fw-bold">Grand Total</td>
                        <td className="text-end fw-bold">₹{fmt(grandTotal)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addRow}>
                    <Plus size={14} className="me-1" />Add Row
                  </button>
                </div>

                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea className="form-control" rows={2} value={form.remarks} onChange={set("remarks")} placeholder="Reason for return" />
                  </div>
                </div>

                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : "Record Return"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default PurchaseReturnModal;
